import { useAuthPages, authPageFailureText, authResponseFailure, type AuthPageFailure } from "../i18n/authPagesPresentation";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Alert } from "../components/ui/Alert";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";

export const ForgotPasswordPage = () => {
  const t = useAuthPages();
  const { data: publicConfig } = usePublicConfig();
  const branding = useBrandingSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<AuthPageFailure | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(authResponseFailure(data?.presentation, data?.message, "requestFailed"));
        return;
      }
      setSubmitted(true);
    } catch {
      setError({ key: "network" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title={t($ => $.forgotTitle)}
          subtitle={t($ => $.forgotSubtitle)}
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t($ => $.forgotHeading)}</h2>
              <p className="text-sm text-muted-foreground">{t($ => $.forgotDescription)}</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {submitted ? (
            <div className="space-y-4">
              <Alert variant="success">
                <div className="space-y-2">
                  <p className="font-medium">{t($ => $.checkEmail)}</p>
                  <p>{t($ => $.forgotSuccess)}</p>
                  <p className="text-xs text-muted-foreground">{t($ => $.forgotExpiry)}</p>
                </div>
              </Alert>
              <Link to="/login">
                <Button type="button" size="lg" className="w-full">
                  {t($ => $.backToLogin)}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
                  {authPageFailureText(error, t)}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.emailAddress)}</label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  {loading ? t($ => $.sending) : t($ => $.sendReset)}
                </Button>
              </form>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                {t($ => $.rememberPassword)} <Link to="/login">{t($ => $.signIn)}</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
